import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  EarlyExitApprovalRequired,
  getAttendanceByDate,
  getAttendanceStatus,
  getMyWorkMode,
  sendWebClockEvent,
} from '../hrms/api/attendance';
import { setAttendancePresence } from '../api/away';
import {
  AttendanceClockAction,
  AttendanceClockRow,
  AttendanceClockState,
  deriveAttendanceClockState,
  getAttendanceUserId,
  getAttendanceUserIds,
  isPastShiftEnd,
  localDayOffset,
  localToday,
} from '../utils/attendanceClock';
import {
  emitAttendanceClockUpdated,
  isAttendanceClockEventForDate,
  isOptimisticAttendanceClockUpdate,
  subscribeAttendanceClockUpdated,
} from '../utils/attendanceClockEvents';
import { isElevated } from '../hrms/utils/rolePermissions';

let attendanceClockSourceCounter = 0;

type PendingClockOverride = {
  clockedIn: boolean;
  generation: number;
};

export function useAttendanceClock(user: any) {
  const sourceIdRef = useRef('');
  if (!sourceIdRef.current) {
    attendanceClockSourceCounter += 1;
    sourceIdRef.current = `attendance-clock-${attendanceClockSourceCounter}`;
  }
  const [nowTick, setNowTick] = useState(() => Date.now());
  const today = useMemo(() => localToday(new Date(nowTick)), [nowTick]);
  const previousDay = useMemo(() => localDayOffset(-1, new Date(nowTick)), [nowTick]);
  const attendanceIdentity = useMemo(
    () => ({
      employee_id: user?.employee_id,
      id: user?.id,
      sub: user?.sub,
      user_id: user?.user_id,
    }),
    [user?.employee_id, user?.id, user?.sub, user?.user_id],
  );
  const userId = useMemo(() => getAttendanceUserId(attendanceIdentity), [attendanceIdentity]);
  const attendanceUserIds = useMemo(() => getAttendanceUserIds(attendanceIdentity), [attendanceIdentity]);
  const [rows, setRows] = useState<AttendanceClockRow[]>([]);
  const [carryoverRows, setCarryoverRows] = useState<AttendanceClockRow[]>([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [loadedAttendanceUserId, setLoadedAttendanceUserId] = useState<string | null>(null);
  const [attendanceRevision, setAttendanceRevision] = useState(0);
  const [modeLoading, setModeLoading] = useState(true);
  const [busy, setBusy] = useState<AttendanceClockAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [workfromhome, setWorkfromhome] = useState(false);
  const [modeOfWorking, setModeOfWorking] = useState<string | null>(null);
  const [isOvertime, setIsOvertime] = useState(false);
  const [overtimeFrom, setOvertimeFrom] = useState<string | null>(null);
  const [overtimeUntil, setOvertimeUntil] = useState<string | null>(null);
  // The employee's shift end ("HH:MM"). In-office Clock In is offered up to
  // this time; after it only overtime re-opens it. null = no bound configured.
  const [officeEnd, setOfficeEnd] = useState<string | null>(null);
  const [dailyStatusState, setDailyStatusState] = useState<AttendanceClockState | null>(null);
  const [confirmedClockedIn, setConfirmedClockedIn] = useState<boolean | null>(null);
  const confirmedClockOverrideRef = useRef<PendingClockOverride | null>(null);
  const confirmedClockGenerationRef = useRef(0);
  const refreshGenerationRef = useRef(0);
  const lastPublishedPresenceRef = useRef<string | null>(null);

  const applyConfirmedClockOverride = useCallback((clockedIn: boolean) => {
    confirmedClockGenerationRef.current += 1;
    confirmedClockOverrideRef.current = {
      clockedIn,
      generation: confirmedClockGenerationRef.current,
    };
    setConfirmedClockedIn(clockedIn);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    confirmedClockOverrideRef.current = null;
    setConfirmedClockedIn(null);
    setDailyStatusState(null);
  }, [today]);

  useEffect(() => {
    let cancelled = false;
    setModeLoading(true);
    getMyWorkMode()
      .then((mode) => {
        if (cancelled) return;
        setWorkfromhome(!!mode.workfromhome);
        setModeOfWorking(mode.mode_of_working);
        setIsOvertime(!!mode.is_overtime);
        setOvertimeFrom(mode.overtime_clock_from ?? null);
        setOvertimeUntil(mode.overtime_clock_until ?? null);
        setOfficeEnd(mode.office_end ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          setWorkfromhome(false);
          setModeOfWorking(null);
          setIsOvertime(false);
          setOvertimeFrom(null);
          setOvertimeUntil(null);
          setOfficeEnd(null);
        }
      })
      .finally(() => {
        if (!cancelled) setModeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isRemote = (modeOfWorking || '').trim().toLowerCase() === 'remote';
  const isHybrid = (modeOfWorking || '').trim().toLowerCase() === 'hybrid';
  const isOvertimeActive = !!(
    isOvertime &&
    (!overtimeFrom || nowTick >= new Date(overtimeFrom).getTime()) &&
    (!overtimeUntil || nowTick < new Date(overtimeUntil).getTime())
  );
  const sessionClock = isRemote || isOvertimeActive || isElevated(user?.role);
  // In-office employees clock in and out in PAIRS through the day; only the
  // shift end stops a NEW punch. `sessionClock` folk have no shift bound.
  const shiftEnded = !sessionClock && isPastShiftEnd(officeEnd, nowTick);
  const canStartNewPunch = sessionClock || !shiftEnded;

  const refresh = useCallback(async () => {
    refreshGenerationRef.current += 1;
    const refreshGeneration = refreshGenerationRef.current;
    const overrideAtStart = confirmedClockOverrideRef.current;
    if (!userId) {
      setRows([]);
      setCarryoverRows([]);
      setDailyStatusState(null);
      confirmedClockOverrideRef.current = null;
      setConfirmedClockedIn(null);
      setLoadedAttendanceUserId(null);
      setRowsLoading(false);
      return;
    }

    setRowsLoading(true);
    try {
      const [todayData, previousDayData, dailyStatus] = await Promise.all([
        getAttendanceByDate(today),
        getAttendanceByDate(previousDay),
        getAttendanceStatus(today).catch(() => null),
      ]);
      if (refreshGeneration !== refreshGenerationRef.current) return;

      const nextRows = Array.isArray(todayData) ? todayData : [];
      const nextCarryoverRows = Array.isArray(previousDayData) ? previousDayData : [];
      setRows(nextRows);
      setCarryoverRows(nextCarryoverRows);
      const myDailyStatus = Array.isArray(dailyStatus)
        ? dailyStatus.find((row: any) =>
            row?.user_id !== null &&
            row?.user_id !== undefined &&
            attendanceUserIds.includes(String(row.user_id)),
          )
        : null;
      const hasClockedIn = !!(
        myDailyStatus?.clock_in_time && myDailyStatus.clock_in_time !== '00:00:00'
      );
      const hasClockedOut = !!(
        myDailyStatus?.out_time && myDailyStatus.out_time !== '00:00:00'
      );
      const onLeave = myDailyStatus?.status === 'Leave';
      let nextDailyStatusState: AttendanceClockState | null = null;
      if (myDailyStatus) {
        const active = hasClockedIn && !hasClockedOut;
        nextDailyStatusState = {
          active,
          // "Done" is now the SHIFT END, not "clocked in and out once" — an
          // in-office employee may do several pairs during office hours.
          doneForToday: !active && !canStartNewPunch,
          hasClockedIn,
          hasClockedOut,
          nextAction: onLeave
            ? null
            : active
              ? 'out'
              : canStartNewPunch
                ? 'in'
                : null,
          onLeave,
        };
      }
      setDailyStatusState(nextDailyStatusState);

      const rawSnapshotState = deriveAttendanceClockState(
        nextRows,
        attendanceUserIds,
        sessionClock,
        nextCarryoverRows,
        shiftEnded,
      );
      const currentOverride = confirmedClockOverrideRef.current;
      if (
        overrideAtStart &&
        currentOverride?.generation === overrideAtStart.generation
      ) {
        const snapshotMatchesOverride = !sessionClock && nextDailyStatusState
          ? overrideAtStart.clockedIn
            ? nextDailyStatusState.active
            : nextDailyStatusState.doneForToday
          : overrideAtStart.clockedIn
            ? rawSnapshotState.active
            : sessionClock
              ? !rawSnapshotState.active
              : rawSnapshotState.doneForToday;
        if (snapshotMatchesOverride) {
          confirmedClockOverrideRef.current = null;
          setConfirmedClockedIn(null);
        }
      }
      setLoadedAttendanceUserId(userId);
      setAttendanceRevision((current) => current + 1);
      setError(null);
    } catch (err: any) {
      if (refreshGeneration !== refreshGenerationRef.current) return;
      setError(err?.message || "Couldn't load attendance state.");
    } finally {
      if (refreshGeneration === refreshGenerationRef.current) {
        setRowsLoading(false);
      }
    }
  }, [attendanceUserIds, canStartNewPunch, previousDay, sessionClock, shiftEnded, today, userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    return subscribeAttendanceClockUpdated((detail) => {
      if (detail.sourceId === sourceIdRef.current) return;
      if (detail.userId && !attendanceUserIds.includes(String(detail.userId))) return;
      if (
        isOptimisticAttendanceClockUpdate(detail) &&
        isAttendanceClockEventForDate(detail, today)
      ) {
        applyConfirmedClockOverride(detail.clockedIn);
      }
      refresh();
    });
  }, [applyConfirmedClockOverride, attendanceUserIds, refresh, today]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const clock = useCallback(
    async (action: AttendanceClockAction) => {
      setBusy(action);
      try {
        const outcome = await sendWebClockEvent(action === 'in', {
          workfromhome,
          overtime: isOvertimeActive,
          ...(isHybrid ? { hybrid: true } : {}),
        });
        // The punch was REFUSED: leaving before the shift ends needs approval.
        // The server says so with a 200 and a status, so nothing below may run
        // — overriding the clock state and telling chat presence the person
        // left would both be lies, and every caller of this hook toasts
        // "Clocked out" on a resolved promise. Throwing is what keeps the
        // sidebar shortcut honest as well as the Attendance page.
        if (outcome.approvalRequired) {
          throw new EarlyExitApprovalRequired(outcome.approvalRequired);
        }
        applyConfirmedClockOverride(action === 'in');
        try {
          await setAttendancePresence(action === 'in');
        } catch {
          // Attendance succeeded; chat presence will self-correct on the next
          // successful bridge call or snapshot refresh.
        }
        await refresh();
        emitAttendanceClockUpdated({
          action,
          clockedIn: action === 'in',
          userId: userId || undefined,
          sourceId: sourceIdRef.current,
        });
        setError(null);
      } catch (err: any) {
        const message = err?.message || `Couldn't clock ${action}.`;
        setError(message);
        throw err;
      } finally {
        setBusy(null);
      }
    },
    [applyConfirmedClockOverride, isHybrid, isOvertimeActive, refresh, userId, workfromhome],
  );

  const derivedState = useMemo(
    () =>
      deriveAttendanceClockState(
        rows,
        attendanceUserIds,
        sessionClock,
        carryoverRows,
        shiftEnded,
      ),
    [attendanceUserIds, carryoverRows, rows, sessionClock, shiftEnded],
  );

  // Keep a confirmed clock action visible while /attendanceAll is catching
  // up; otherwise the sidebar can offer Clock In immediately after clock-in.
  const state = useMemo(() => {
    if (confirmedClockedIn === null) {
      // Approved leave is authoritative for the current day and makes Clock In
      // read-only in both the sidebar and the full Attendance page.
      if (dailyStatusState?.onLeave) return dailyStatusState;
      // A real current-day Absent row means this employee has not started a
      // session today. It must win even for remote/overtime clocks: otherwise
      // an unmatched raw punch from yesterday leaves the header on Clock Out
      // while today's status table correctly shows Absent.
      if (
        dailyStatusState &&
        !dailyStatusState.hasClockedIn &&
        !dailyStatusState.hasClockedOut
      ) {
        return dailyStatusState;
      }
      // The per-day status endpoint is the authoritative settled result for a
      // completed session. /attendanceAll can briefly lag after clock-out and
      // still contain only the clock-in row, which would incorrectly keep the
      // shortcut on Clock Out despite the header already showing an out-time.
      if (!sessionClock && dailyStatusState) return dailyStatusState;
      if (sessionClock && dailyStatusState?.hasClockedOut && !dailyStatusState.active) {
        return dailyStatusState;
      }
      return derivedState;
    }

    if (confirmedClockedIn) {
      return {
        ...derivedState,
        active: true,
        doneForToday: false,
        hasClockedIn: true,
        nextAction: 'out' as const,
        onLeave: false,
      };
    }

    // Just clocked OUT. Whether another punch can start is the shift window's
    // call, not "you already clocked in once today".
    return {
      ...derivedState,
      active: false,
      doneForToday: !canStartNewPunch,
      hasClockedIn: true,
      hasClockedOut: true,
      nextAction: canStartNewPunch ? 'in' as const : null,
      onLeave: false,
    };
  }, [canStartNewPunch, confirmedClockedIn, dailyStatusState, derivedState, sessionClock]);

  useEffect(() => {
    if (loadedAttendanceUserId !== userId || !userId || error) return;

    const presenceKey = `${userId}:${state.active}`;
    if (lastPublishedPresenceRef.current === presenceKey) return;

    lastPublishedPresenceRef.current = presenceKey;
    void setAttendancePresence(state.active).catch(() => {
      if (lastPublishedPresenceRef.current === presenceKey) {
        lastPublishedPresenceRef.current = null;
      }
    });
  }, [attendanceRevision, error, loadedAttendanceUserId, state.active, userId]);

  return {
    ...state,
    busy,
    clock,
    error,
    loading: rowsLoading || modeLoading,
    requiresLocation: !state.onLeave && !isRemote && !isOvertimeActive,
    refresh,
    userId,
    // The same value the ordinary punch sends (see sendWebClockEvent above).
    // Exposed so the face check-in modal records In Office / WFH identically
    // instead of re-deriving it from storage and disagreeing with this one.
    workfromhome,
    // Same reason: the face modal has to know a hybrid employee may clock in
    // without a location, exactly as the ordinary punch does.
    isHybrid,
    // Shift context, so the UI can say WHY there is no button and label an
    // overtime punch as one rather than showing a bare "Clock In".
    isOvertimeActive,
    officeEnd,
    shiftEnded,
  };
}
